// fx.ts — 演出パイプライン (P2 design §7)。tableGame のイベントジャーナル (§6) を読み、
// 「何を・いつ・どう見せ、何を鳴らすか」を決める純関数 planFx と、その実行系 useTableFx を持つ。
//
// 方針 (§7.1): 盤面 DOM は最終状態を即描画する。動きは
//   (a) 牌単位の CSS mount アニメ — riverDelays / latestDiscard を board へ渡すだけ (時間軸ジョブにしない)
//   (b) オーバーレイ (帯・役満カットイン・配牌バナー) + 段階リザルト — FxJob キューを単一 setTimeout
//       チェーンで消化して FxState を進める
// の二系統。lagged presented-state 機械は作らない。リザルトと帯が時間軸の主役。
//
// 人間のツモ牌 (§7.1): reducer の仕様上、自分の draw は自分の行動と同一バッチで届くので、
// ジャーナル駆動にせず SoloMahjong 側の humanDrawn() ピークスロット再 mount (`key={journal.head}` +
// fx-draw-in) で演出する。planFx は自分の draw からジョブを一切作らない (「自分の discard と
// 同バッチの自分の draw をスキップ」が常態。tsumo/カンの draw も win/call ジョブ側が拾う)。
//
// 依存 (§7.5 受け入れ基準): React と tableGame.ts (型) と sound.ts のみ。
// @mj/protocol / @mj/engine からは **型 import だけ** (ランタイム依存なし)。
import { useEffect, useRef, useState } from 'react';
import type { ScoreResult, Seat } from '@mj/protocol';
import type { GameEvent } from '@mj/engine';
import type { EventJournal, JournalEntry, TileId, WaitKind } from '../play/tableGame';
import { sfx, type SfxName } from './sound';

// ---------------------------------------------------------------------------
// 型 (§7.2)
// ---------------------------------------------------------------------------

export type BandTone =
  | 'riichi' | 'openRiichi' | 'infiniteRiichi'
  | 'pon' | 'chii' | 'kan'
  | 'ron' | 'tsumo' | 'ryuukyoku';

/** ジョブに紐づく効果音。detuneCents は tick の段階ピッチ用 (sound.ts 報告書: step×69 cents)。 */
export interface FxSound { name: SfxName; atMs: number; detuneCents?: number }

export interface BandPayload { text: string; tone: BandTone; seat: Seat | null }

interface FxJobBase {
  /** 由来 entry の journal seq (デバッグ・テスト用。一意ではない — 1 entry が複数 job を生む)。 */
  seq: number;
  /** reduced 時は planFx が代替値で生成 (§7.4 — JS 側の duration 代替テーブル)。 */
  durationMs: number;
  /** true: durationMs 待ってから次ジョブ / false: 即次へ (自己クリアは別タイマー)。 */
  blocking: boolean;
  sounds: FxSound[];
  /** aria-live に流す一文 (「CPU2がポン」等)。 */
  aria?: string;
}

export type FxJob = FxJobBase & (
  | { kind: 'band'; payload: BandPayload }
  | { kind: 'cutin'; payload: { names: string[] } } // 役満。キャラ絵スロットは空 div (§0-7)
  | { kind: 'resultStage'; payload: { stage: number; final: boolean } }
  | { kind: 'delta'; payload: { deltas: { seat: Seat; amount: number }[] } }
  | { kind: 'dealIntro'; payload: { banner: string } }
  | { kind: 'noop'; payload: null }
);

export interface FxState {
  band: { id: number; text: string; tone: BandTone; seat: Seat | null } | null;
  cutin: { id: number; names: string[] } | null;
  /** -1=非表示, 0=帯後モーダル, 1..n=役行, n+1=翻, n+2=点, n+3=移動 (流局は 0=モーダル, 1=支払い)。 */
  resultStage: number;
  deltas: { seat: Seat; amount: number; id: number }[];
  riverDelays: Record<string, number>;
  dealing: { id: number; banner: string } | null; // 「東1局 2本場」
  latestDiscard: { seat: Seat; index: number } | null; // 白リング (D3)
}

export const INITIAL_FX: FxState = {
  band: null, cutin: null, resultStage: -1, deltas: [],
  riverDelays: {}, dealing: null, latestDiscard: null,
};

export type RiverKey = `${number}:${number}`;

/** planFx の追加入力 (§7.2 のシグネチャに対し additive・省略可)。 */
export interface PlanOptions {
  /** 席ごとの「このスライス前までの捨て牌数」。journal slice は局の途中から始まるので、河の
   *  絶対 index (`${seat}:${discardIndex}`) を出すにはフック側がこれを持ち回る必要がある。
   *  省略時は 0 始まり (slice が handStart を含めば planFx 内で自動リセットされる)。 */
  riverBase?: readonly number[];
  /** 役 id → 表示名 (カタログ写像、yakuName.ts の makeResultName 互換)。省略時は id 素通し。 */
  nameOf?: (id: string, han: number) => string;
}

export interface FxPlan {
  jobs: FxJob[];
  riverDelays: Record<RiverKey, number>; // `${seat}:${discardIndex}` → animation-delay ms
  sounds: FxSound[];                     // 着地音 (バッチ整列オフセット付き) — plan 時に即発火する
  /** §7.2 返り値への additive 拡張: フックが即時 state に反映する分。 */
  latestDiscard: { seat: Seat; index: number } | null;
  nextRiverBase: number[];               // 次スライスの PlanOptions.riverBase に渡す
}

// ---------------------------------------------------------------------------
// 確定値テーブル (§7.3。reduced 列は「reduced 代替」)
// ---------------------------------------------------------------------------

const MS = {
  riverStep: 160,           // A2: バッチ内 n 番目の打牌 delay n×160ms
  bandRiichi: 1400,         // A5 複合 blocking 計
  bandRiichiReduced: 600,   //   帯中央静的 600ms
  bandCall: 760,            // A6: pop 160 + 保持 450 + fade 150
  bandCallReduced: 450,     //   帯静的 450ms
  bandRyuukyoku: 1000,      // A12
  bandRyuukyokuReduced: 600,
  doraFlip: 350,            // A7 (非 blocking — カン由来は帯の後ろに自然に並ぶ)
  doraFlipReduced: 200,
  cutinBase: 2200,          // A9: in 600 + 保持 1200 + fade 400
  cutinStagger: 250,        //   複数役満 250ms ずらし
  cutinReduced: 1000,       //   金パネル静的 1000ms
  stageModal: 200,          // A8②
  stageModalReduced: 150,
  stageRow: 320,            // A8③ (8 行超は 5 行目以降 160ms)
  stageRowFast: 160,
  stagePoints: 850,         // A8⑤: ロール 700 + 確定ポップ 150
  delta: 900,               // A10 非 blocking
  dealIntro: 1100,          // A11 計
  dealIntroReduced: 800,    //   バナー静的のみ
} as const;

/** 渋滞圧縮 (§7.2): ランナーが取り出し時の残キュー長で blocking duration に掛ける係数。 */
export function congestionScale(queueLen: number): number {
  return queueLen > 12 ? 0.25 : queueLen > 6 ? 0.5 : 1;
}

// ---------------------------------------------------------------------------
// planFx — 純関数 (§7.2)。journal entries → ジョブ列 + 河ディレイ + 着地音。
// ---------------------------------------------------------------------------

const WIND_JA: Record<string, string> = { z1: '東', z2: '南', z3: '西', z4: '北' };

function bannerOf(roundWind: TileId, dealer: Seat, honba: number): string {
  const w = WIND_JA[roundWind] ?? '東';
  return honba > 0 ? `${w}${dealer + 1}局 ${honba}本場` : `${w}${dealer + 1}局`;
}

function seatName(seat: Seat, humanSeat: Seat): string {
  return seat === humanSeat ? 'あなた' : `CPU${seat}`;
}

// win の直前 engine イベント (handStart を越えない)。winner 自身の draw 直後ならツモ、それ以外はロン。
function prevEngineEvent(entries: readonly JournalEntry[], i: number): GameEvent | null {
  for (let k = i - 1; k >= 0; k--) {
    const ev = entries[k]!.ev;
    if (ev.kind === 'engine') return ev.e;
    if (ev.kind === 'handStart') break;
  }
  return null;
}

// 役満カットインの表示名。ScoreResult.yaku の役満行は han = 13×mult で届く (yakuName.ts 流儀) ので
// han>=13 を役満行とみなす。カタログ写像 (nameOf) はフック経由で integrate が渡す — planFx は
// engine/カタログへのランタイム依存を持たない (§7.5)。
function yakumanNames(results: ScoreResult[], nameOf: (id: string, han: number) => string): string[] {
  const out: string[] = [];
  for (const r of results) {
    if (r.yakuman <= 0) continue;
    const ymRows = r.yaku.filter((y) => y.han >= 13);
    for (const y of (ymRows.length > 0 ? ymRows : r.yaku)) {
      const n = nameOf(y.id, y.han);
      if (!out.includes(n)) out.push(n);
    }
  }
  return out.length > 0 ? out : ['役満'];
}

export function planFx(
  entries: readonly JournalEntry[],
  humanSeat: Seat,
  reduced: boolean,
  opts: PlanOptions = {},
): FxPlan {
  const nameOf = opts.nameOf ?? ((id: string): string => id);
  const base: number[] = [...(opts.riverBase ?? [])]; // 席→捨て牌数 (河の絶対 index 復元用)
  const jobs: FxJob[] = [];
  const riverDelays: Record<RiverKey, number> = {};
  const sounds: FxSound[] = [];
  let latestDiscard: { seat: Seat; index: number } | null = null;
  let stagger = 0;     // スライス内の打牌カスケード順 (A2: n×160ms。handStart でリセット)
  let lastWinRows = 0; // 直前 win の役行数 — handEnd の最終 stage (rows+3) 算出用

  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i]!;
    const seq = entry.seq;
    const ev = entry.ev;

    if (ev.kind === 'handStart') {
      base.fill(0); // 新しい局 → 河は空 (絶対 index は 0 から)
      stagger = 0;
      const banner = bannerOf(ev.roundWind, ev.dealer, ev.honba);
      jobs.push({
        seq, kind: 'dealIntro', payload: { banner },
        durationMs: reduced ? MS.dealIntroReduced : MS.dealIntro,
        blocking: true,
        // A11: 配牌ザラッ + ④ドラ表示牌 (A7) のきらめきをカスケード後に。
        sounds: [{ name: 'deal', atMs: 0 }, { name: 'doraFlip', atMs: reduced ? 400 : 700 }],
        aria: `${banner} 開始`,
      });
      continue;
    }

    if (ev.kind === 'handEnd') {
      if (ev.result === 'win') {
        // A8⑥/A10: 最終段 (rows+3) = 点数移動。win と同スライスが常態 (finishHand は commit 直後に
        // 同期で走る)。万一 win が前スライスに落ちても rows=0 → stage 3 で final にはなる。
        jobs.push({
          seq, kind: 'resultStage', payload: { stage: lastWinRows + 3, final: true },
          durationMs: 0, blocking: false, sounds: [],
        });
      } else {
        // A12 流局: 帯は engine 'ryuukyoku' イベント側 (二重に出さない)。ここはモーダル + 支払い。
        jobs.push({
          seq, kind: 'resultStage', payload: { stage: 0, final: false },
          durationMs: reduced ? MS.stageModalReduced : MS.stageModal, blocking: true, sounds: [],
        });
        jobs.push({
          seq, kind: 'resultStage', payload: { stage: 1, final: true },
          durationMs: 0, blocking: false, sounds: [],
        });
      }
      const deltas = ev.deltas
        .map((amount, seat) => ({ seat: seat as Seat, amount }))
        .filter((d) => d.amount !== 0);
      if (deltas.length > 0) {
        jobs.push({
          seq, kind: 'delta', payload: { deltas },
          durationMs: MS.delta, blocking: false,
          sounds: [{ name: 'scoreMove', atMs: 0 }],
        });
      }
      continue;
    }

    if (ev.kind === 'gameOver') {
      jobs.push({ seq, kind: 'noop', payload: null, durationMs: 0, blocking: false, sounds: [], aria: 'トビで終局' });
      continue;
    }

    // ---- engine イベント ----
    const e = ev.e;
    switch (e.event) {
      case 'deal':
        break; // 局頭スナップショットは handStart が正 (journal 報告書 §3)
      case 'draw':
        // 自分の draw はスキップ (§7.1: humanDrawn() ピークスロットの mount アニメが担当。同バッチに
        // 自分の discard が続くのが常態で、tsumo/カン時もそれぞれの job が時間軸を持つ)。
        // 他家の draw も時間軸ジョブにしない — A4 の fx-turn パルスは integrate が SeatBoard の
        // turnSeat prop (生の game state) で結線する。ジョブ化すると CPU 周回でキューが膨れ、
        // 渋滞圧縮 (>6) が誤発火するため。
        break;
      case 'discard': {
        // A1/A2: 河セルの着地はジョブではなく「mount アニメ + animation-delay」。React 1 コミットで
        // 最終状態を描き、CSS が時間差を演出する (§7.2 キュー機構)。
        const idx = base[e.seat] ?? 0;
        base[e.seat] = idx + 1;
        const delay = reduced ? 0 : stagger * MS.riverStep;
        stagger += 1;
        riverDelays[`${e.seat}:${idx}`] = delay;
        // 立直宣言打牌は強打 (discardStrong)。riichi イベントは同一 reduce バッチで discard の直後に
        // 並ぶ (reducer L193-194) ので 1 個先読みで足りる。
        const next = entries[i + 1]?.ev;
        const isRiichiTile = next !== undefined && next.kind === 'engine'
          && next.e.event === 'riichi' && next.e.seat === e.seat;
        sounds.push({ name: isRiichiTile ? 'discardStrong' : 'discard', atMs: delay });
        latestDiscard = { seat: e.seat, index: idx };
        break;
      }
      case 'riichi':
        // A5。エンジンの riichi イベントは open/infinite を運ばない (types.ts L47) ため tone は常に
        // 'riichi' — openRiichi / infiniteRiichi トーンは型・色 (--band-open-riichi 等) だけ先に用意
        // してあり、イベント拡張時に planFx のここを分岐するだけでよい。
        jobs.push({
          seq, kind: 'band', payload: { text: '立直', tone: 'riichi', seat: e.seat },
          durationMs: reduced ? MS.bandRiichiReduced : MS.bandRiichi,
          blocking: true,
          sounds: [{ name: 'riichi', atMs: 0 }],
          aria: `${seatName(e.seat, humanSeat)}が立直`,
        });
        break;
      case 'call': {
        if (e.kind === 'kita') break; // 北抜きはルーチン操作 — 帯にしない (A 表対象外)
        const text = e.kind === 'pon' ? 'ポン' : e.kind === 'chii' ? 'チー' : 'カン';
        const tone: BandTone = e.kind === 'pon' ? 'pon' : e.kind === 'chii' ? 'chii' : 'kan';
        const sound: SfxName = e.kind === 'pon' ? 'callPon' : e.kind === 'chii' ? 'callChii' : 'callKan';
        jobs.push({
          seq, kind: 'band', payload: { text, tone, seat: e.seat },
          durationMs: reduced ? MS.bandCallReduced : MS.bandCall,
          blocking: true,
          sounds: [{ name: sound, atMs: 0 }],
          aria: `${seatName(e.seat, humanSeat)}が${text}`,
        });
        break;
      }
      case 'doraFlip':
        // A7: めくり自体は board の mount アニメ (fx-flip)。ここは音とペーシングのみ。blocking な
        // カン帯の後ろに並ぶので「カン由来は A6 帯の後ろへキューイング」が自然に成立する。
        jobs.push({
          seq, kind: 'noop', payload: null,
          durationMs: reduced ? MS.doraFlipReduced : MS.doraFlip,
          blocking: false,
          sounds: [{ name: 'doraFlip', atMs: 0 }],
        });
        break;
      case 'flower':
        break; // 花抜きもルーチン操作 — 演出なし
      case 'win': {
        const res = e.results[0]; // 多重ロンは head の結果で段階数を決める (deltas は handEnd が正)
        const rows = res?.yaku.length ?? 0;
        const han = res?.han ?? 0;
        const ym = e.results.reduce((m, r) => Math.max(m, r.yakuman), 0);
        lastWinRows = rows;
        const winner = (e.winners[0] ?? humanSeat) as Seat;
        const prev = prevEngineEvent(entries, i);
        const tsumo = prev !== null && prev.event === 'draw' && e.winners.includes(prev.seat);
        // A6/A8①: ロン・ツモ宣言帯。役満はファンファーレをカットイン側に譲る (重ね鳴り回避)。
        jobs.push({
          seq, kind: 'band',
          payload: { text: tsumo ? 'ツモ' : 'ロン', tone: tsumo ? 'tsumo' : 'ron', seat: winner },
          durationMs: reduced ? MS.bandCallReduced : MS.bandCall,
          blocking: true,
          sounds: ym > 0 ? [] : [{ name: 'winFanfare', atMs: 0 }],
          aria: `${seatName(winner, humanSeat)}が${tsumo ? 'ツモ' : 'ロン'}`,
        });
        // A9: 役満カットインは A8② (モーダル) の前。
        if (ym > 0) {
          const names = yakumanNames(e.results, nameOf);
          jobs.push({
            seq, kind: 'cutin', payload: { names },
            durationMs: reduced ? MS.cutinReduced : MS.cutinBase + MS.cutinStagger * Math.max(0, names.length - 1),
            blocking: true,
            sounds: [{ name: 'yakumanFanfare', atMs: 0 }],
            aria: `役満 ${names.join('・')}`,
          });
        }
        // A8 段階開示: 0=モーダル → 1..rows=役行 → rows+1=翻 → rows+2=点。rows+3 (移動) は handEnd 側。
        jobs.push({
          seq, kind: 'resultStage', payload: { stage: 0, final: false },
          durationMs: reduced ? MS.stageModalReduced : MS.stageModal, blocking: true, sounds: [],
        });
        if (!reduced) { // reduced: 全行・最終値即表示 — 中間段は出さず handEnd の final へ直行
          for (let row = 1; row <= rows; row++) {
            jobs.push({
              seq, kind: 'resultStage', payload: { stage: row, final: false },
              durationMs: rows > 8 && row >= 5 ? MS.stageRowFast : MS.stageRow,
              blocking: true, sounds: [],
            });
          }
          // A8④ 翻カウントアップ: min(80ms×han, 800ms)。tick は段階ピッチ (≈69 cents/step、sound.ts
          // 報告書 §3) を detuneCents で乗せる。視覚の rAF カウンタは integrate のモーダル側。
          const hanMs = Math.min(80 * Math.max(1, han), 800);
          const ticks = Math.min(Math.max(1, han), 10);
          const tickSounds: FxSound[] = [];
          for (let t = 0; t < ticks; t++) {
            tickSounds.push({ name: 'tick', atMs: Math.round((hanMs / ticks) * t), detuneCents: t * 69 });
          }
          jobs.push({
            seq, kind: 'resultStage', payload: { stage: rows + 1, final: false },
            durationMs: hanMs, blocking: true, sounds: tickSounds,
          });
          jobs.push({
            seq, kind: 'resultStage', payload: { stage: rows + 2, final: false },
            durationMs: MS.stagePoints, blocking: true, sounds: [],
          });
        }
        break;
      }
      case 'ryuukyoku':
        // A12: 「流局」帯 (A5 構造・--band-ryuukyoku)。モーダル/支払いは handEnd 側。
        jobs.push({
          seq, kind: 'band', payload: { text: '流局', tone: 'ryuukyoku', seat: null },
          durationMs: reduced ? MS.bandRyuukyokuReduced : MS.bandRyuukyoku,
          blocking: true, sounds: [], aria: '流局',
        });
        break;
    }
  }

  return { jobs, riverDelays, sounds, latestDiscard, nextRiverBase: base };
}

// ---------------------------------------------------------------------------
// useTableFx — 実行系 (§7.2 キュー機構)。journal.head 検知 → sliceFrom → planFx → キュー消化。
// reduced は引数で受ける (settings は import しない — 実効値は shell が D15 で算出する)。
// ---------------------------------------------------------------------------

export function useTableFx(
  // structural: any PlayTable (LocalTable / RemoteTable) or a bare TableGame satisfies this.
  game: { journal: EventJournal; humanSeat: Seat; wait: WaitKind } | null,
  reduced: boolean,
  /** 役 id → 表示名 (makeResultName(catalog) 互換)。integrate が渡す。省略時は id 素通し。 */
  nameOf?: (id: string, han: number) => string,
): { fx: FxState; skip: () => void; ariaLines: string[] } {
  const [fx, setFx] = useState<FxState>(INITIAL_FX);
  const [ariaLines, setAriaLines] = useState<string[]>([]);

  // 全部 ref — ランナーは render を跨いで単一の時間軸を持つ。
  const fxRef = useRef<FxState>({ ...INITIAL_FX });
  const cursorRef = useRef(0);
  const queueRef = useRef<FxJob[]>([]);
  const blockingRef = useRef(false);
  const timersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const aliveRef = useRef(true);
  const idRef = useRef(0);
  const riverBaseRef = useRef<number[]>([]);
  const gameRef = useRef(game);
  const nameOfRef = useRef(nameOf);
  gameRef.current = game; // TableGame は可変シングルトン — wait の現在値はいつでも生で読める
  nameOfRef.current = nameOf;

  const commitFx = (): void => setFx({ ...fxRef.current });
  const pushAria = (line: string): void => setAriaLines((prev) => [...prev, line].slice(-6)); // 直近6件

  const schedule = (fn: () => void, ms: number): void => {
    const id = setTimeout(() => {
      timersRef.current.delete(id);
      if (aliveRef.current) fn();
    }, ms);
    timersRef.current.add(id); // 全タイマー Set 管理 (§7.2 アンマウント安全)
  };

  /** ジョブの state 反映 + (silent でなければ) 音。視覚のクリアは blocking 満了 / skip 側で行う。 */
  const applyJob = (job: FxJob, silent: boolean): void => {
    if (job.aria !== undefined) pushAria(job.aria);
    if (!silent) {
      for (const s of job.sounds) {
        sfx.play(s.name, { delayMs: s.atMs, ...(s.detuneCents !== undefined ? { detuneCents: s.detuneCents } : {}) });
      }
    }
    const st = fxRef.current;
    switch (job.kind) {
      case 'band':
        st.band = { id: ++idRef.current, ...job.payload };
        break;
      case 'cutin':
        st.cutin = { id: ++idRef.current, names: job.payload.names };
        break;
      case 'resultStage':
        st.resultStage = job.payload.stage;
        break;
      case 'delta': {
        const tagged = job.payload.deltas.map((d) => ({ ...d, id: ++idRef.current }));
        st.deltas = [...st.deltas, ...tagged];
        const ids = new Set(tagged.map((d) => d.id));
        schedule(() => { // A10 非 blocking — 自己クリア (900ms)
          fxRef.current.deltas = fxRef.current.deltas.filter((d) => !ids.has(d.id));
          commitFx();
        }, MS.delta);
        break;
      }
      case 'dealIntro':
        // 新しい局 — 前局のリザルト・帯・河ディレイを一掃してからバナーを出す。
        st.dealing = { id: ++idRef.current, banner: job.payload.banner };
        st.resultStage = -1;
        st.deltas = [];
        st.band = null;
        st.cutin = null;
        st.latestDiscard = null;
        st.riverDelays = {};
        break;
      case 'noop':
        break;
    }
  };

  /** blocking 満了時に消える視覚 (帯/カットイン/バナー)。 */
  const clearVisual = (job: FxJob): void => {
    const st = fxRef.current;
    if (job.kind === 'band') st.band = null;
    else if (job.kind === 'cutin') st.cutin = null;
    else if (job.kind === 'dealIntro') st.dealing = null;
  };

  /** 単一 setTimeout チェーン (§7.2): blocking は満了でクリア&次へ、非 blocking は即次へ。 */
  const pump = (): void => {
    if (!aliveRef.current || blockingRef.current) return;
    const q = queueRef.current;
    let dirty = false;
    while (q.length > 0) {
      const job = q.shift()!;
      applyJob(job, false);
      dirty = true;
      if (job.blocking) {
        // 渋滞圧縮: 取り出し時の残キュー長で ×0.5 / ×0.25 (§7.2)。
        let dur = job.durationMs * congestionScale(q.length);
        // 人間入力待ち (turn/call) 中の帯は保持を 60% に短縮 — 操作を待たせない。
        const g = gameRef.current;
        if (job.kind === 'band' && g !== null && (g.wait === 'turn' || g.wait === 'call')) dur *= 0.6;
        blockingRef.current = true;
        schedule(() => {
          blockingRef.current = false;
          clearVisual(job);
          commitFx();
          pump();
        }, dur);
        break;
      }
    }
    if (dirty) commitFx();
  };

  /** タップで現行 blocking ジョブ + リザルト段階を最終形へ早送り (§7.2)。音は鳴らさない。 */
  const skip = (): void => {
    for (const t of timersRef.current) clearTimeout(t);
    timersRef.current.clear();
    blockingRef.current = false;
    const q = queueRef.current;
    while (q.length > 0) applyJob(q.shift()!, true); // resultStage は最終段まで進む / delta も即適用
    const st = fxRef.current;
    st.band = null;
    st.cutin = null;
    st.dealing = null;
    if (st.deltas.length > 0) {
      // 早送りで適用した delta の自己クリアタイマーも上で消えているので、一括掃除を仕掛け直す。
      schedule(() => { fxRef.current.deltas = []; commitFx(); }, MS.delta);
    }
    commitFx();
  };

  // 対局リセット (§7.2): game が替わったら cursor=0・queue 破棄・FxState 初期化。
  // cursor 0 始まりなので handStart から再走査 → 配牌アニメが必ず再生される。
  useEffect(() => {
    aliveRef.current = true;
    cursorRef.current = 0;
    queueRef.current = [];
    blockingRef.current = false;
    riverBaseRef.current = [];
    for (const t of timersRef.current) clearTimeout(t);
    timersRef.current.clear();
    fxRef.current = { ...INITIAL_FX };
    setFx({ ...INITIAL_FX });
    setAriaLines([]);
    return () => { // cleanup: 全 clear + alive 落とし + queue 破棄 (§7.2 アンマウント安全)
      aliveRef.current = false;
      for (const t of timersRef.current) clearTimeout(t);
      timersRef.current.clear();
      queueRef.current = [];
    };
  }, [game]);

  // 検知 (§7.2): journal.head を依存値に、cursor からの差分を planFx してキューへ。
  const head = game !== null ? game.journal.head : -1;
  useEffect(() => {
    if (game === null) return;
    const entries = game.journal.sliceFrom(cursorRef.current);
    if (entries.length === 0) return;
    cursorRef.current = game.journal.head;
    const opts: PlanOptions = { riverBase: riverBaseRef.current };
    const nm = nameOfRef.current;
    if (nm !== undefined) opts.nameOf = nm;
    const plan = planFx(entries, game.humanSeat, reduced, opts);
    riverBaseRef.current = plan.nextRiverBase;
    // 河の時間差・白リングは時間軸ジョブではなく即時 state — DOM は最終状態を即描画する (§7.1)。
    fxRef.current.riverDelays = plan.riverDelays;
    if (plan.latestDiscard !== null) fxRef.current.latestDiscard = plan.latestDiscard;
    // 着地音はバッチ整列オフセット (atMs = riverDelays と同値) 付きで即スケジュール。
    for (const s of plan.sounds) {
      sfx.play(s.name, { delayMs: s.atMs, ...(s.detuneCents !== undefined ? { detuneCents: s.detuneCents } : {}) });
    }
    queueRef.current.push(...plan.jobs);
    commitFx();
    pump();
  }, [game, head, reduced]); // pump/commitFx は ref 駆動の毎 render 再生成 — 依存に入れない

  return { fx, skip, ariaLines };
}
